import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import AuthGuard from './components/AuthGuard';
import AuthLayout from './components/authentication/AuthLayout';
import DashboardLayout from './components/dashboard/DashboardLayout';
import GuestGuard from './components/GuestGuard';
import LoadingScreen from './components/LoadingScreen';
import MainLayout from './components/MainLayout';
import * as userPermissions from './consts/userRoles';
import useAuth from './hooks/useCustomAuth';
import LJDashboardLayout from './pages/LJ/Layout/DashboardLayout';
import LJMainPage from './pages/LJ/Pages/MainPage';
import LJTrainingDetailPage from './pages/LJ/Pages/TrainingDetail';
import LJFAQs from './pages/LJ/Pages/FAQs';
import LJTimeline from './pages/LJ/Pages/Timeline';

const Loadable = (Component) => (props) => (
  <Suspense fallback={<LoadingScreen />}>
    <Component {...props} />
  </Suspense>
);

// Authentication pages
const Login = Loadable(lazy(() => import('./pages/authentication/Login')));
const PasswordRecovery = Loadable(lazy(() => import('./pages/authentication/PasswordRecovery')));
const PasswordReset = Loadable(lazy(() => import('./pages/authentication/PasswordReset')));
const Register = Loadable(lazy(() => import('./pages/authentication/Register')));
const VerifyCode = Loadable(lazy(() => import('./pages/authentication/VerifyCode')));

// Dashboard pages
const Profile = Loadable(lazy(() => import('./pages/dashboard/profile/Profile')));
const Hours = Loadable(lazy(() => import('./pages/dashboard/personal/hours/Hours')));
const HoursByProject = Loadable(lazy(() => import('./pages/dashboard/personal/hours/HoursByProject')));
const Vacations = Loadable(lazy(() => import('./pages/dashboard/personal/vacations/Vacations')));
const ProfileList = Loadable(lazy(() => import('./pages/dashboard/profile/ProfileList')));
const OpportunityDetail = Loadable(lazy(() => import('./pages/dashboard/recruitment/opportunities/OpportunityDetail')));
const OpportunityCandidate = Loadable(lazy(() => import('./pages/dashboard/recruitment/opportunities/OpportunityCandidate')));
const Opportunities = Loadable(lazy(() => import('./pages/dashboard/recruitment/opportunities/Opportunities')));
const Process = Loadable(lazy(() => import('./pages/dashboard/recruitment/process/Process')));
const ProcessDetail = Loadable(lazy(() => import('./pages/dashboard/recruitment/process/ProcessDetail')));
const StaffReport = Loadable(lazy(() => import('./pages/dashboard/recruitment/staffReport/StaffReport')));
const StaffRequest = Loadable(lazy(() => import('./pages/dashboard/recruitment/staffRequest/StaffRequest')));
const YearlyEvaluation = Loadable(lazy(() => import('./pages/dashboard/evaluation/YearlyEvaluation')));
const DashboardKPI = Loadable(lazy(() => import('./pages/dashboard/evaluation/DashboardKPI'))); // NEW
const YearlyEvaluationProposals = Loadable(lazy(() => import('./pages/dashboard/evaluation/YearlyEvaluation/Proposals')));
const PeriodicEvaluation = Loadable(lazy(() => import('./pages/dashboard/evaluation/PeriodicEvaluation')));
const Harmonizations = Loadable(lazy(() => import('./pages/dashboard/evaluation/Harmonizations')));
const AdminYearlyEvaluation = Loadable(lazy(() => import('./pages/dashboard/evaluation/YearlyEvaluation/admin')));
const FeedbackContinuousList = Loadable(lazy(() => import('./pages/dashboard/evaluation/FeedbackContinuous')));
const YearlyEvaluationForm = Loadable(lazy(() => import('./pages/dashboard/evaluation/YearlyEvaluation/YearlyEvalForm')));
const CollectibleEval = Loadable(lazy(() => import('./pages/dashboard/evaluation/YearlyEvaluation/CollectiveEval')));
const EvaluationPlanning = Loadable(lazy(() => import('./pages/dashboard/evaluation/EvaluationPlanning/EvaluationPlanning')));
const EvaluationPlanningDashboard = Loadable(lazy(() => import('./pages/dashboard/evaluation/EvaluationPlanning/EvaluationPlanningDashboard')));
const LandingPage = Loadable(lazy(() => import('./pages/dashboard/recruitment/landingPage/LandingPage')));
// const ApplicationPage = Loadable(lazy(() => import('./pages/dashboard/recruitment/ApplicationPage')));
const CandidateList = Loadable(lazy(() => import('./pages/dashboard/recruitment/candidates/Candidates')));
const ObjectivesManagement = Loadable(lazy(() => import('./pages/dashboard/evaluation/Objectives/ObjectivesManagement')));
const ObjectivesEval = Loadable(lazy(() => import('./pages/dashboard/evaluation/Objectives/ObjectivesEval')));
const ObjectivesDetail = Loadable(lazy(() => import('./pages/dashboard/evaluation/Objectives/ObjectivesDetail')));
const ProjectList = Loadable(lazy(() => import('./pages/dashboard/project/ProjectList')));
const ProjectDetail = Loadable(lazy(() => import('./pages/dashboard/project/ProjectDetail')));
const PipelineDocuments = Loadable(lazy(() => import('./pages/dashboard/evaluation/PipelineDocuments')));
const VacationsApproval = Loadable(lazy(() => import('./pages/dashboard/personal/vacations/VacationsApproval')));
const VacationsCalendarTeam = Loadable(lazy(() => import('./pages/dashboard/personal/vacations/VacationsCalendarTeam')));
const Competencies = Loadable(lazy(() => import('./pages/dashboard/evaluation/Competencies/Competencies')));
const CompetenciesDetail = Loadable(lazy(() => import('./pages/dashboard/evaluation/Competencies/CompetenciesDetail')));
const CompetenciesCatalog = Loadable(lazy(() => import('./pages/dashboard/evaluation/Competencies/CompetenciesCatalog')));
const Notifications = Loadable(lazy(() => import('./pages/dashboard/notification/NotificationListTable')));
const TrainingCatalogue = Loadable(lazy(() => import('./pages/dashboard/trainingCatalogue/trainingCatalogue')));
const TrainingDetail = Loadable(lazy(() => import('./pages/dashboard/trainingCatalogue/TrainingDetail')));
const Training = Loadable(lazy(() => import('./pages/dashboard/trainingCatalogue/User')));
const TrainingDetail2 = Loadable(lazy(() => import('./pages/dashboard/trainingCatalogue/User/TrainingDetail2')));
const TrainingEventDetail = Loadable(lazy(() => import('./pages/dashboard/trainingCatalogue/TrainingEventDetail')));
const TrainingApprovalList = Loadable(lazy(() => import('./pages/dashboard/trainingCatalogue/User/TrainingApprovalList')));
const EntitiesList = Loadable(lazy(() => import('./pages/dashboard/entities/EntitiesList')));
const CompanyMap = Loadable(lazy(() => import('./pages/dashboard/companyMap/CompanyMap')));
const PBIDashboard = Loadable(lazy(() => import('./pages/dashboard/evaluation/PBIDashboards/PBIDashboard')));

// Docs pages

const SignaturePage = Loadable(lazy(() => import('./pages/signature/SignaturePage')));

// Error pages
const AuthorizationRequired = Loadable(lazy(() => import('./pages/AuthorizationRequired')));
const NotFound = Loadable(lazy(() => import('./pages/NotFound')));
const ServerError = Loadable(lazy(() => import('./pages/ServerError')));

const Roles = Loadable(lazy(() => import('./pages/dashboard/RoleConfiguration')));
const RoleConfiguration = Loadable(lazy(() => import('./pages/dashboard/RoleConfiguration/RoleConfiguration')));

const routes = () => {
  const auth = useAuth();
  const defaultPath = auth?.user?.defaultRoute || '/dashboard/profile/myprofile';

  return [
    {
      path: "LJ",
      element: <AuthGuard roles={userPermissions.PORTAL}>
        <LJDashboardLayout />
      </AuthGuard>,
      children: [
        {
          path: "/",
          element: <LJMainPage />
        },
        {
          path: "/timeline",
          element: <LJTimeline />
        },
        {
          path: "/FAQ",
          children: [
            {
              path: "/",
              element: <LJFAQs />
            },
            {
              path: "/:page",
              element: <LJFAQs />
            }
          ]
        },
        {
          path: "/detail",
          children: [
            {
              path: "/",
              element: <LJTrainingDetailPage />
            },
            {
              path: "/:id",
              element: <LJTrainingDetailPage />
            },
          ]
        }
      ]
    },
    {
      path: 'authentication',
      element: (
        <GuestGuard>
          <AuthLayout />
        </GuestGuard>
      ),
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        },
        {
          path: 'login-unguarded',
          element: <Login />
        },
        {
          path: 'password-recovery',
          element: <PasswordRecovery />
        },
        {
          path: 'password-reset',
          element: <PasswordReset />
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          )
        },
        {
          path: 'register-unguarded',
          element: <Register />
        },
        {
          path: 'verify-code',
          element: <VerifyCode />
        },
      ]
    },
    {
      path: 'LandingPage/:oid',
      element: (
        <GuestGuard>
          <LandingPage />
        </GuestGuard>
      )
    },
    {
      path: 'LandingPage/:oid/:cid',
      element: (
        <GuestGuard>
          <LandingPage />
        </GuestGuard>
      )
    },
    {
      path: 'dashboard',
      element: (
        <AuthGuard roles={userPermissions.PORTAL}>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: 'management',
          children: [
            {
              path: 'timesheet/:username',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_TEAM_HOURS_CONTROL}>
                  <Hours managementView />
                </AuthGuard>
              )
            },
            {
              path: 'timesheet',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_TEAM_HOURS_CONTROL_BY_PROJECT}>
                  <HoursByProject />
                </AuthGuard>
              )
            }
          ]
        },
        {
          path: 'profile',
          children: [
            {
              path: 'MyProfile',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PROFILE_REGISTRATION}>
                  <Profile />
                </AuthGuard>
              )
            },
            {
              path: 'myTimesheet',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PROFILE_HOURS}>
                  <Hours />
                </AuthGuard>
              )
            },
            {
              path: 'myVacations',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PROFILE_VACATIONS}>
                  <Vacations />
                </AuthGuard>
              )
            },
            {
              path: 'myVacationsApproval',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PROFILE_AUTO_APPROVAL_VACATIONS}>
                  <Vacations autoApproval />
                </AuthGuard>
              )
            },
            {
              path: 'myNotifications',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PROFILE}>
                  <Notifications />
                </AuthGuard>
              )
            }
          ]
        },
        {
          path: 'training',
          children: [
            {
              path: "list",
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PROFILE_TRAINING}>
                  <Training />
                </AuthGuard>
              )
            },
            {
              path: "detail/:id",
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PROFILE_TRAINING}>
                  <TrainingDetail2 />
                </AuthGuard>
              )
            }
          ]
        },
        {
          path: 'team',
          children: [
            {
              path: 'timesheet',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_TEAM_HOURS_CONTROL}>
                  <Hours teamView viewType="TEAM" />
                </AuthGuard>
              ),
            },
            {
              path: 'timesheet/:username',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_TEAM_HOURS_CONTROL}>
                  <Hours teamView />
                </AuthGuard>
              )
            },
            {
              path: 'timesheet/:username/:month/:year',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_TEAM_HOURS_CONTROL}>
                  <Hours teamView />
                </AuthGuard>
              )
            },
            {
              path: 'vacationApproval',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_TEAM_VACATIONS_APPROVAL}>
                  <VacationsApproval />
                </AuthGuard>
              )
            },
            {
              path: 'vacations',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_TEAM_VACATIONS_APPROVAL}>
                  <VacationsCalendarTeam teamView />
                </AuthGuard>
              )
            },
            {
              path: 'vacations/:username/:year',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_TEAM_VACATIONS_APPROVAL}>
                  <Vacations teamView />
                </AuthGuard>
              )
            },
            {
              path: 'training',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_TEAM_TRAINING_APPROVAL}>
                  <TrainingApprovalList teamView />
                </AuthGuard>
              )
            },
            {
              path: 'evaluation/YearlyEvaluation/DashboardKPI',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_EVAL_TEAM_DASHBOARD}>
                  <DashboardKPI viewType="TEAM" />
                </AuthGuard>
              )
            },
            {
              path: 'evaluation/YearlyEvaluation/DashboardKPI/:username',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_EVAL_TEAM_DASHBOARD}>
                  <DashboardKPI viewType="TEAM" />
                </AuthGuard>
              )
            },
          ]
        },
        {
          path: 'signature',
          element: <SignaturePage />
        },
        {
          path: 'recruitment',
          children: [
            {
              path: 'process/:pid/detail',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_RECRUITMENT_PROCESS}>
                  <ProcessDetail />
                </AuthGuard>
              )
            },
            {
              path: 'process/detail',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_RECRUITMENT_PROCESS}>
                  <ProcessDetail />
                </AuthGuard>
              )
            },
            {
              path: 'opportunities',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_RECRUITMENT_ACCESS}>
                  <Opportunities opportunityViewMode="GLOBAL" />
                </AuthGuard>
              )
            },
            {
              path: 'myOpportunities',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_RECRUITMENT_ACCESS}>
                  <Opportunities opportunityViewMode="ME" />
                </AuthGuard>
              )
            },
            {
              path: 'opportunities/:oid/detail',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_RECRUITMENT_ACCESS}>
                  <OpportunityDetail />
                </AuthGuard>
              )
            },
            {
              path: 'opportunities/detail',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_RECRUITMENT_ACCESS}>
                  <OpportunityDetail />
                </AuthGuard>
              )
            },
            {
              path: 'opportunities/detail/:sid',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_RECRUITMENT_OPPORTUNITY}>
                  <OpportunityDetail />
                </AuthGuard>
              )
            },
            {
              path: 'opportunities/:oid/detail/:tab',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_RECRUITMENT_ACCESS}>
                  <OpportunityDetail />
                </AuthGuard>
              )
            },
            {
              path: 'opportunities/:oid/candidate',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_RECRUITMENT_ACCESS}>
                  <OpportunityCandidate />
                </AuthGuard>
              )
            },
            {
              path: 'opportunities/:oid/candidate/:cid',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_RECRUITMENT_ACCESS}>
                  <OpportunityCandidate />
                </AuthGuard>
              )
            },
            {
              path: 'process',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_RECRUITMENT_PROCESS}>
                  <Process />
                </AuthGuard>
              )
            },
            {
              path: 'candidates',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_RECRUITMENT_OPPORTUNITY}>
                  <CandidateList />
                </AuthGuard>
              )
            },
            {
              path: 'staffReportRh',
              element: (
                <AuthGuard
                  roles={userPermissions.PORTAL_RECRUITMENT_STAFF_REPORT_MANAGEMENT}
                  moduleID="STAFFREPORT"
                >
                  <StaffReport viewType="HR" />
                </AuthGuard>
              )
            },
            {
              path: 'staffReportManagement',
              element: (
                <AuthGuard
                  roles={userPermissions.PORTAL_RECRUITMENT_STAFF_REPORT}
                  moduleID="STAFFREPORT"
                >
                  <StaffReport viewType="MANAGEMENT" />
                </AuthGuard>
              )
            },
            {
              path: 'staffRequest/',
              element: (
                <AuthGuard
                  roles={userPermissions.PORTAL_RECRUITMENT_OPPORTUNITY}
                  moduleID="STAFFREPORT"
                >
                  <StaffRequest />
                </AuthGuard>
              )
            },
            {
              path: 'staffRequest/:requestId/detail',
              element: (
                <AuthGuard
                  roles={userPermissions.PORTAL_RECRUITMENT_STAFF_REPORT}
                  moduleID="STAFFREPORT"
                >
                  <StaffRequest viewMode="MY_REQUESTS" />
                </AuthGuard>
              )
            },
            {
              path: 'staffRequest/:requestId/pending',
              element: (
                <AuthGuard
                  roles={userPermissions.PORTAL_RECRUITMENT_STAFF_REPORT_MANAGEMENT}
                  moduleID="STAFFREPORT"
                >
                  <StaffRequest viewMode="PENDING_REQUESTS" />
                </AuthGuard>
              )
            },
            {
              path: 'staffRequest/:requestId/open',
              element: (
                <AuthGuard
                  roles={userPermissions.PORTAL_RECRUITMENT_STAFF_REPORT_MANAGEMENT}
                  moduleID="STAFFREPORT"
                >
                  <StaffRequest viewMode="APPROVED_REQUESTS" />
                </AuthGuard>
              )
            }
          ]
        },
        {
          path: 'admin',
          children: [
            {
              path: 'evaluation/YearlyEvaluation/dashboard',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PERF_EVAL_ADMINISTRATION}>
                  <AdminYearlyEvaluation />
                </AuthGuard>
              )
            },
            {
              path: 'evaluation/FeedbackContinuous',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_FEEDBACK_CONTINUOUS_LIST}>
                  <FeedbackContinuousList />
                </AuthGuard>
              )
            },
            {
              path: 'employees',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_BACKOFFICE_EMPLOYEE}>
                  <ProfileList />
                </AuthGuard>
              )
            },
            {
              path: 'employees/:pid',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_BACKOFFICE_EMPLOYEE}>
                  <Profile />
                </AuthGuard>
              )
            },
            {
              path: 'roleConfiguration',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_ADMINISTRATION_ROLES}>
                  <Roles />
                </AuthGuard>
              )
            },
            {
              path: 'roleConfiguration/:id',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_ADMINISTRATION_ROLES}>
                  <RoleConfiguration />
                </AuthGuard>
              )
            },
            {
              path: 'entities',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_BACKOFFICE_ENTITIES}>
                  <EntitiesList />
                </AuthGuard>
              )
            },
            {
              path: 'entities/:pid',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_BACKOFFICE_ENTITIES}>
                  <EntitiesList />
                </AuthGuard>
              )
            },
            {
              path: 'companyMap',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_BACKOFFICE_COMPANY_MAP}>
                  <CompanyMap />
                </AuthGuard>
              )
            },
            {
              path: 'projects',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_ADMINISTRATION_PROJECTS}>
                  <ProjectList />
                </AuthGuard>
              )
            }, {
              path: 'projects/:pid/detail',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_ADMINISTRATION_PROJECTS}>
                  <ProjectDetail />
                </AuthGuard>
              )
            },
            {
              path: 'projects/detail',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_ADMINISTRATION_PROJECTS}>
                  <ProjectDetail />
                </AuthGuard>
              )
            },
            {
              path: 'timesheet',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_ADMINISTRATION_HOURS_CONTROL}>
                  <Hours globalView legacy />
                </AuthGuard>
              )
            },
            {
              path: 'vacations',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PROFILE_VACATIONS}>
                  <VacationsCalendarTeam globalView />
                </AuthGuard>
              )
            },
            {
              path: 'evaluation/YearlyEvaluation/DashboardKPI',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_EVAL_ADMIN_DASHBOARD}>
                  <DashboardKPI viewType="GLOBAL" />
                </AuthGuard>
              )
            },
            {
              path: 'evaluation/YearlyEvaluation/DashboardKPI/:username',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_EVAL_ADMIN_DASHBOARD}>
                  <DashboardKPI viewType="GLOBAL" />
                </AuthGuard>
              )
            },
          ]
        },
        {
          path: 'evaluation',
          children: [
            {
              path: 'YearlyEvaluation/MyEvaluation',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PERF_EVAL_DASHBOARD}>
                  <YearlyEvaluation viewType="ME" />
                </AuthGuard>
              )
            },
            {
              path: 'PeriodicEvaluation/MyEvaluation',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PERF_EVAL_DASHBOARD}>
                  <PeriodicEvaluation viewType="ME" />
                </AuthGuard>
              )
            },
            {
              path: 'YearlyEvaluation/DashboardKPI/:username',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PERF_EVAL_DASHBOARD}>
                  <DashboardKPI viewType="TEAM" />
                </AuthGuard>
              )
            },
            {
              path: 'YearlyEvaluation/TeamEvaluation',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PERF_EVAL_TEAM}>
                  <YearlyEvaluation viewType="TEAM" />
                </AuthGuard>
              )
            },
            {
              path: 'YearlyEvaluation/proposals',
              element: (
                <AuthGuard
                  roles={userPermissions.PORTAL_PERF_EVAL_TEAM}
                  moduleID="HARMONIZATION"
                >
                  <YearlyEvaluationProposals />
                </AuthGuard>
              )
            },
            {
              path: 'YearlyEvaluation/collective',
              exact: true,
              element: <Navigate to="/dashboard/evaluation/YearlyEvaluation/TeamEvaluation" />
            },
            {
              path: 'YearlyEvaluation/collective/:id',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PERF_EVAL_TEAM}>
                  <CollectibleEval />
                </AuthGuard>
              )
            },
            {
              path: 'YearlyEvaluation/form/:id',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PERF_EVAL_DASHBOARD}>
                  <YearlyEvaluationForm />
                </AuthGuard>
              )
            },
            {
              // Lista de Feedbacks Contínuos: só leitura (ganha à rota :view por ser estática)
              path: 'YearlyEvaluation/form/:id/FC',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_FEEDBACK_CONTINUOUS_LIST}>
                  <YearlyEvaluationForm feedbackListView />
                </AuthGuard>
              )
            },
            {
              path: 'YearlyEvaluation/form/:id/:view',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PERF_EVAL_ADMINISTRATION}>
                  <YearlyEvaluationForm />
                </AuthGuard>
              )
            },
            {
              path: 'Objectives/ObjectivesEvaluation',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_GOALS_MANAGEMENT}>
                  <ObjectivesEval />
                </AuthGuard>
              )
            },
            {
              path: 'Objectives/ObjectivesManagement/',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_GOALS_MANAGEMENT}>
                  <ObjectivesManagement />
                </AuthGuard>
              )
            },
            {
              path: 'Objectives/ObjectivesManagement/:user',
              key: 'objectives_detail_management',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_GOALS_MANAGEMENT}>
                  <ObjectivesDetail
                    key="objectives_detail_management"
                    userView={false}

                  />
                </AuthGuard>
              )
            },
            {
              path: 'Objectives/ObjectivesManagement/:user/:evaluationCycle',
              key: 'objectives_detail_management',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_GOALS_MANAGEMENT}>
                  <ObjectivesDetail
                    key="objectives_detail_management"
                    userView={false}
                  />
                </AuthGuard>
              )
            },
            {
              path: 'Objectives/MyObjectives',
              key: 'objectives_detail',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PERF_EVAL_DASHBOARD}>
                  <ObjectivesDetail
                    key="objectives_detail"
                    userView
                    viewType="INDIVIDUAL"
                  />
                </AuthGuard>
              )
            },
            {
              path: 'Competencies/CompetenciesManagement',
              key: 'competencies',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_GOALS_MANAGEMENT}>
                  <Competencies
                    key="competencies"
                    viewType="TEAM"
                  />
                </AuthGuard>
              )
            },
            {
              path: 'Competencies/CompetenciesManagement/:eid/:evaluationCycle',
              key: 'competencies_detail',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PERF_EVAL_DASHBOARD}>
                  <CompetenciesDetail
                    key="competencies_detail"
                    viewType="INDIVIDUAL"
                  />
                </AuthGuard>
              )
            },
            {
              path: 'Competencies/CompetenciesCatalog',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PERF_EVAL_ADMINISTRATION}>
                  <CompetenciesCatalog />
                </AuthGuard>
              )
            },
            {
              path: 'EvaluationPlanning/detail',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PERF_EVAL_ADMINISTRATION}>
                  <EvaluationPlanning />
                </AuthGuard>
              )
            },
            {
              path: 'EvaluationPlanning/:mid/detail',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PERF_EVAL_ADMINISTRATION}>
                  <EvaluationPlanning />
                </AuthGuard>
              )
            },
            {
              path: 'EvaluationPlanning',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PERF_EVAL_ADMINISTRATION}>
                  <EvaluationPlanningDashboard />
                </AuthGuard>
              )
            },
            {
              path: 'PeriodicEvaluation/TeamEvaluation',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PERF_EVAL_TEAM}>
                  <PeriodicEvaluation viewType="TEAM" />
                </AuthGuard>
              )
            },
            {
              path: 'PipelineDocuments/MyDocuments',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_EVALUATION_DOCUMENTS_MANAGEMENT}>
                  <PipelineDocuments viewType="ME" />
                </AuthGuard>
              )
            },
            {
              path: 'PipelineDocuments/TeamDocuments',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_EVALUATION_DOCUMENTS_MANAGEMENT}>
                  <PipelineDocuments viewType="TEAM" />
                </AuthGuard>
              )
            },
            {
              path: 'Harmonizations',
              element: (
                <AuthGuard
                  roles={userPermissions.PORTAL_EVALUATION_CCA_EVALUATION_SAVE}
                  moduleID="HARMONIZATION"
                >
                  <Harmonizations />
                </AuthGuard>
              )
            },
            {
              path: 'dashboard',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_PERF_EVAL_ADMINISTRATION}>
                  <PBIDashboard
                    reportId={process.env.REACT_APP_HUMAM_RESOURCES_PBI_DASHBOARD_REPORT_ID}
                    groupId={process.env.REACT_APP_HUMAM_RESOURCES_PBI_DASHBOARD_GROUP_ID}
                    label="Recursos Humanos"
                  />
                </AuthGuard>
              )
            },
          ]
        },
        {
          path: "hr",
          children: [
            {
              path: 'trainingCatalogue',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_ADMINISTRATION_TRAINING}>
                  <TrainingCatalogue globalView />
                </AuthGuard>
              )
            },
            {
              path: 'trainingCatalogue/detail',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_ADMINISTRATION_TRAINING}>
                  <TrainingDetail />
                </AuthGuard>
              )
            },
            {
              path: 'trainingCatalogue/:id/detail',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_ADMINISTRATION_TRAINING}>
                  <TrainingDetail />
                </AuthGuard>
              )
            },
            {
              path: 'trainingEvent/detail',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_ADMINISTRATION_TRAINING}>
                  <TrainingEventDetail />
                </AuthGuard>
              )
            },
            {
              path: 'trainingEvent/:id/detail',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_ADMINISTRATION_TRAINING}>
                  <TrainingEventDetail />
                </AuthGuard>
              )
            },
            {
              path: 'vacations',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_VACATIONS_ADMINISTRATION}>
                  <Vacations hrView />
                </AuthGuard>
              )
            },
          ]
        },
        {
          path: "learningJourney",
          children: [
            {
              path: 'dashboard',
              element: (
                <AuthGuard roles={userPermissions.PORTAL_LEARNING_JOURNEY}>
                  <PBIDashboard
                    reportId={process.env.REACT_APP_LEARNING_JOURNEY_PBI_DASHBOARD_REPORT_ID}
                    groupId={process.env.REACT_APP_LEARNING_JOURNEY_PBI_DASHBOARD_GROUP_ID}
                    label="Learning Journey"
                  />
                </AuthGuard>
              )
            },
          ]
        }
      ]
    },
    {
      path: '*',
      element: <MainLayout />,
      children: [
        {
          path: '/',
          exact: true,
          element: <Navigate to={defaultPath} />
        },
        {
          path: '*',
          element: <Navigate to="error/404" />
        }
      ]
    },
    {
      path: 'error',
      element: <AuthLayout />,
      children: [
        {
          path: '/',
          exact: true,
          element: <Navigate to={defaultPath} />
        },
        {
          path: '401',
          element: <AuthorizationRequired />
        },
        {
          path: '404',
          element: <NotFound />
        },
        {
          path: '500',
          element: <ServerError />
        },
        {
          path: '*',
          element: <NotFound />
        }
      ]
    }
  ];
};

export default routes;
